import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  TextField,
  Typography,
  Grid,
  FormControlLabel,
  Switch,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Save, ArrowBack, Image as ImageIcon } from '@mui/icons-material';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import {
  GET_TRAINING_SAMPLE,
  GET_TRAINING_SAMPLE_WITH_DISEASE,
  UPDATE_TRAINING_SAMPLE,
} from '../graphql/operations';
import { 
  TrainingSample, 
  UpdateTrainingSampleInput, 
  ReviewStatus, 
  ValidationQuality, 
  ImageQuality, 
  DiagnosisClarity,
  Disease 
} from '../types';
import { formatDateString } from '../utils/dateUtils';
import { useSignedImage } from '../utils/useSignedImage';
import { DiseaseSelector } from './DiseaseSelector';
import { FullScreenImageViewer } from './FullScreenImageViewer';

const TrainingSampleDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<UpdateTrainingSampleInput>({});
  const [selectedDisease, setSelectedDisease] = useState<Disease | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [imageViewerOpen, setImageViewerOpen] = useState(false);

  // First query - basic data without disease
  const { data: basicData, loading: basicLoading, error: basicError, refetch: refetchBasic } = useQuery(GET_TRAINING_SAMPLE, {
    variables: { id },
    skip: !id,
    errorPolicy: 'all',
  });

  // Second query - with disease data, only if label_id exists
  const { data: diseaseData, loading: diseaseLoading, refetch: refetchDisease } = useQuery(GET_TRAINING_SAMPLE_WITH_DISEASE, {
    variables: { id },
    skip: !id || !basicData?.getTrainingSample?.label_id,
    errorPolicy: 'all',
    onCompleted: (data) => {
      if (data?.getTrainingSample?.disease) {
        setSelectedDisease(data.getTrainingSample.disease);
      }
    }
  });

  // Combine the data
  const data = diseaseData || basicData;
  const loading = basicLoading || diseaseLoading;
  const error = basicError;

  // Set form data when basic data loads
  useEffect(() => {
    if (basicData?.getTrainingSample) {
      const sample = basicData.getTrainingSample;
      setFormData({
        symptoms_description: sample.symptoms_description || '',
        prediction: sample.prediction || '',
        confidence: sample.confidence || 0,
        model_version: sample.model_version || '',
        ai_summary: sample.ai_summary || '',
        review_status: sample.review_status || 'PENDING',
        correct_diagnosis: sample.correct_diagnosis || '',
        staff_notes: sample.staff_notes || '',
        is_validated: sample.is_validated ?? false,
        validation_quality: sample.validation_quality,
        use_for_training: sample.use_for_training ?? false,
        training_category: sample.training_category || '',
        image_quality: sample.image_quality,
        diagnosis_clarity: sample.diagnosis_clarity,
        label_id: sample.label_id || null,
      });
    }
  }, [basicData]);

  // Refetch function that refetches both queries
  const refetch = () => {
    const promises = [];
    if (refetchBasic) promises.push(refetchBasic());
    if (refetchDisease) promises.push(refetchDisease());
    return Promise.all(promises);
  };

  // Generate signed URL for the image - MUST be called before any conditional returns
  const sample = data?.getTrainingSample;
  const { signedUrl: signedImageUrl, loading: imageLoading, error: imageError } = useSignedImage(sample?.original_image_url);

  const [updateSample] = useMutation(UPDATE_TRAINING_SAMPLE, {
    onCompleted: (data) => {
      setSaving(false);
      if (data.updateTrainingSample === true) {
        setSuccessMessage('Training sample updated successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
        refetch();
      } else {
        console.error('Update failed - received:', data.updateTrainingSample);
      }
    },
    onError: (error) => {
      setSaving(false);
      console.error('Error updating training sample:', error);
    }
  });

  // Separate mutation for auto-saving disease selection
  const [updateDiseaseLabel] = useMutation(UPDATE_TRAINING_SAMPLE, {
    onCompleted: (data) => {
      if (data.updateTrainingSample === true) {
        setSuccessMessage('Disease label updated!');
        setTimeout(() => setSuccessMessage(''), 2000);
        // Refetch to get the updated disease info
        refetch();
      } else {
        console.error('Disease label update failed - received:', data.updateTrainingSample);
      }
    },
    onError: (error) => {
      console.error('Error updating disease label:', error);
    }
  });

  const handleInputChange = (field: keyof UpdateTrainingSampleInput, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!id) return;
    
    setSaving(true);
    try {
      await updateSample({
        variables: {
          id,
          input: formData
        }
      });
    } catch (error) {
      console.error('Save error:', error);
      setSaving(false);
    }
  };

  const handleBack = () => {
    // Use browser's native back functionality for proper scroll restoration
    window.history.back();
  };

  const getStatusColor = (status: ReviewStatus) => {
    switch (status) {
      case ReviewStatus.PENDING:
        return 'warning';
      case ReviewStatus.IN_REVIEW:
        return 'info';
      case ReviewStatus.REVIEWED:
        return 'primary';
      case ReviewStatus.APPROVED:
        return 'success';
      case ReviewStatus.REJECTED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getQualityColor = (quality?: ValidationQuality) => {
    switch (quality) {
      case ValidationQuality.EXCELLENT:
        return 'success';
      case ValidationQuality.GOOD:
        return 'primary';
      case ValidationQuality.FAIR:
        return 'warning';
      case ValidationQuality.POOR:
        return 'error';
      default:
        return 'default';
    }
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">Error loading training sample: {error.message}</Alert>;
  if (!sample) return <Alert severity="error">Training sample not found</Alert>;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={handleBack}
          >
            Back to List
          </Button>
          <Typography variant="h4" component="h1">
            Training Sample #{sample.id.slice(-8)}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Save />}
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </Box>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {successMessage}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Image Section */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Sample Image
              </Typography>
              {imageLoading ? (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: 200,
                    bgcolor: 'grey.100',
                    borderRadius: 1
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : signedImageUrl ? (
                <CardMedia
                  component="img"
                  image={signedImageUrl}
                  alt="Training sample"
                  onClick={() => setImageViewerOpen(true)}
                  sx={{ 
                    width: '100%', 
                    height: 'auto',
                    maxHeight: 400,
                    objectFit: 'contain',
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.02)',
                    }
                  }}
                />
              ) : imageError ? (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: 200,
                    bgcolor: 'error.light',
                    borderRadius: 1,
                    flexDirection: 'column',
                    p: 2
                  }}
                >
                  <ImageIcon sx={{ fontSize: 48, color: 'error.main', mb: 1 }} />
                  <Typography variant="caption" color="error.main" textAlign="center">
                    Failed to load image: {imageError}
                  </Typography>
                </Box>
              ) : (
                <Box 
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    height: 200,
                    bgcolor: 'grey.100',
                    borderRadius: 1
                  }}
                >
                  <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Status and Quality Info */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Status & Quality
              </Typography>
              <Box sx={{ mb: 2 }}>
                <Chip
                  label={sample.review_status}
                  color={getStatusColor(sample.review_status)}
                  sx={{ mr: 1, mb: 1 }}
                />
                {sample.validation_quality && (
                  <Chip
                    label={sample.validation_quality}
                    color={getQualityColor(sample.validation_quality)}
                    sx={{ mr: 1, mb: 1 }}
                  />
                )}
              </Box>
              <Typography variant="body2" color="text.secondary">
                <strong>Created:</strong> {formatDateString(sample.created_at)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Updated:</strong> {formatDateString(sample.updated_at)}
              </Typography>
              {sample.reviewed_at && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Reviewed:</strong> {formatDateString(sample.reviewed_at)}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Main Form */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                AI Analysis
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Prediction"
                    value={formData.prediction || ''}
                    onChange={(e) => handleInputChange('prediction', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Confidence"
                    type="number"
                    inputProps={{ min: 0, max: 1, step: 0.01 }}
                    value={formData.confidence || ''}
                    onChange={(e) => handleInputChange('confidence', parseFloat(e.target.value))}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Model Version"
                    value={formData.model_version || ''}
                    onChange={(e) => handleInputChange('model_version', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Training Category"
                    value={formData.training_category || ''}
                    onChange={(e) => handleInputChange('training_category', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Symptoms Description"
                    multiline
                    rows={2}
                    value={formData.symptoms_description || ''}
                    onChange={(e) => handleInputChange('symptoms_description', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="AI Summary"
                    multiline
                    rows={3}
                    value={formData.ai_summary || ''}
                    onChange={(e) => handleInputChange('ai_summary', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Staff Review
              </Typography>
              
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                  <DiseaseSelector
                    value={selectedDisease}
                    onChange={async (disease) => {
                      setSelectedDisease(disease);
                      handleInputChange('label_id', disease?.id || undefined);
                      
                      // Also update the correct_diagnosis field with the disease name
                      if (disease) {
                        handleInputChange('correct_diagnosis', disease.name);
                      } else {
                        handleInputChange('correct_diagnosis', '');
                      }
                      
                      // Auto-save the disease selection and correct diagnosis
                      if (id) {
                        try {
                          await updateDiseaseLabel({
                            variables: {
                              id,
                              input: { 
                                label_id: disease?.id || null,
                                correct_diagnosis: disease?.name || ''
                              }
                            }
                          });
                        } catch (error) {
                          console.error('Failed to auto-save disease label:', error);
                        }
                      }
                    }}
                    label="Disease Label"
                    placeholder="Search for a disease by name or ICD-10 code..."
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Review Status</InputLabel>
                    <Select
                      value={formData.review_status || ''}
                      label="Review Status"
                      onChange={(e) => handleInputChange('review_status', e.target.value as ReviewStatus)}
                    >
                      {Object.values(ReviewStatus).map((status) => (
                        <MenuItem key={status} value={status}>{status}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Validation Quality</InputLabel>
                    <Select
                      value={formData.validation_quality || ''}
                      label="Validation Quality"
                      onChange={(e) => handleInputChange('validation_quality', e.target.value as ValidationQuality)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {Object.values(ValidationQuality).map((quality) => (
                        <MenuItem key={quality} value={quality}>{quality}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Image Quality</InputLabel>
                    <Select
                      value={formData.image_quality || ''}
                      label="Image Quality"
                      onChange={(e) => handleInputChange('image_quality', e.target.value as ImageQuality)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {Object.values(ImageQuality).map((quality) => (
                        <MenuItem key={quality} value={quality}>{quality}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Diagnosis Clarity</InputLabel>
                    <Select
                      value={formData.diagnosis_clarity || ''}
                      label="Diagnosis Clarity"
                      onChange={(e) => handleInputChange('diagnosis_clarity', e.target.value as DiagnosisClarity)}
                    >
                      <MenuItem value="">None</MenuItem>
                      {Object.values(DiagnosisClarity).map((clarity) => (
                        <MenuItem key={clarity} value={clarity}>{clarity}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Correct Diagnosis"
                    value={formData.correct_diagnosis || ''}
                    onChange={(e) => handleInputChange('correct_diagnosis', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Staff Notes"
                    multiline
                    rows={3}
                    value={formData.staff_notes || ''}
                    onChange={(e) => handleInputChange('staff_notes', e.target.value)}
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                Training Settings
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_validated || false}
                        onChange={(e) => handleInputChange('is_validated', e.target.checked)}
                      />
                    }
                    label="Is Validated"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.use_for_training || false}
                        onChange={(e) => handleInputChange('use_for_training', e.target.checked)}
                      />
                    }
                    label="Use for Training"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Full Screen Image Viewer */}
      {sample && (
        <FullScreenImageViewer
          open={imageViewerOpen}
          onClose={() => setImageViewerOpen(false)}
          imageUrl={signedImageUrl || undefined}
          sample={sample}
        />
      )}
    </Box>
  );
};

export default TrainingSampleDetail;